import { ResponsiveBar } from '@nivo/bar'

import ChartLine, { LineAxis } from './components/Line'
import { useLineChartActions } from './context'
import { useChartTheme } from './useChartTheme'

export const MARGIN_LEFT = {
  percentage: 85,
  count: 85,
  money: 115,
}

const MARGIN_RIGHT = 115

const BAR_COLORS = {
  credits: '#22c55e',
  cost: '#ef4444',
}

const SERIES_LABELS = {
  credits: 'Credits',
  cost: 'Cost',
  balance: 'Balance',
  min: 'Min',
  max: 'Max',
  avg: 'Avg',
}

const DEFAULT_LINE_COLOR = '#3b82f6'

const AXIS_LABELS = {
  money: 'Cost/Credits',
  percentage: '%',
  count: 'Count',
}

export default function LineChart({
  data: { data = [], keys = [], format, type } = {},
  lineMetric = 'balance',
  lineColor = DEFAULT_LINE_COLOR,
  lineMetrics = [lineMetric],
  lineLabels = SERIES_LABELS,
  lineColors = {},
  onLineMetricChange,
  barMinValue = 'auto',
  barMaxValue = 'auto',
  lineMinValue = 0,
  lineMaxValue = 1,
}) {
  const { actions: { dateFilter, setDateFilter } } = useLineChartActions()
  const { nivoTheme, mutedText, gridStroke, axisStroke } = useChartTheme()
  const leftMargin = MARGIN_LEFT[type] || MARGIN_LEFT.money

  const handleClick = datum => {
    const { dateStart, dateEnd } = datum.data
    const nextFilter = {
      label: datum.indexValue,
      start: dateStart,
      end: dateEnd,
    }

    setDateFilter(dateFilter?.label === nextFilter.label ? null : nextFilter)
  }

  const handleLineLegendClick = datum => {
    onLineMetricChange?.(datum.id)
  }

  const renderTooltip = ({ id, value, color, indexValue, data: rowData }) => <div className="rounded-xs twy-bg-base-accent twy-shadow-base twy-text-base-accent px-2.5 py-1.5 text-sm">
      <div className="twy-text-base mb-1">{indexValue}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />
        <span>{SERIES_LABELS[id] || id}: {format ? format(value) : value}</span>
      </div>
      {id !== lineMetric && rowData[lineMetric] != null && <div className="flex items-center gap-2 mt-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: lineColor }} />
          <span>{SERIES_LABELS[lineMetric] || lineMetric}: {format ? format(rowData[lineMetric]) : rowData[lineMetric]}</span>
        </div>
      }
    </div>

  return (
    <div className="relative h-full overflow-hidden rounded border twy-border-base twy-bg-base p-1">
      <ResponsiveBar
        data={data}
        keys={keys}
        indexBy="date"
        groupMode="grouped"
        valueScale={{ type: 'linear' }}
        minValue={barMinValue}
        maxValue={barMaxValue}
        theme={{
          ...nivoTheme,
          grid: {
            line: {
              stroke: gridStroke,
              strokeWidth: 1,
            },
          },
        }}
        margin={{
          top: 70,
          right: MARGIN_RIGHT,
          bottom: 100,
          left: leftMargin,
        }}
        padding={0.3}
        innerPadding={2}
        colors={({ id }) => BAR_COLORS[id] || '#1f77b4'}
        borderRadius={2}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: -90,
          legend: 'date interval',
          legendPosition: 'middle',
          legendOffset: 90,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: AXIS_LABELS[type] || type,
          legendPosition: 'middle',
          legendOffset: -(leftMargin - 20),
          format,
        }}
        enableGridY
        enableGridX={false}
        enableLabel={false}
        isInteractive
        animate
        onClick={handleClick}
        legends={[
          {
            anchor: 'top-left',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -18,
            itemsSpacing: 12,
            itemWidth: 80,
            itemHeight: 16,
            itemDirection: 'left-to-right',
            itemOpacity: 1,
            itemTextColor: mutedText,
            symbolSize: 10,
            symbolShape: 'circle',
            data: keys.map(key => ({
              id: key,
              label: SERIES_LABELS[key] || key,
              color: BAR_COLORS[key],
            })),
          },
          {
            anchor: 'top-right',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -18,
            itemsSpacing: 12,
            itemWidth: 70,
            itemHeight: 16,
            itemDirection: 'left-to-right',
            itemOpacity: 1,
            itemTextColor: mutedText,
            symbolSize: 10,
            symbolShape: 'circle',
            onClick: handleLineLegendClick,
            data: lineMetrics.map(metric => ({
              id: metric,
              label: lineLabels[metric] || metric,
              color: metric === lineMetric ? lineColors[metric] || lineColor : mutedText,
            })),
          },
        ]}
        layers={[
          'grid',
          'axes',
          'bars',
          props => <ChartLine
            {...props}
            data={data}
            metric={lineMetric}
            color={lineColor}
            lineMinValue={lineMinValue}
            lineMaxValue={lineMaxValue}
            renderTooltip={renderTooltip}
          />,
          props => <LineAxis
            {...props}
            barMinValue={barMinValue}
            barMaxValue={barMaxValue}
            lineMinValue={lineMinValue}
            lineMaxValue={lineMaxValue}
            format={format}
            label={lineLabels[lineMetric] || lineMetric}
            labelOffset={MARGIN_RIGHT - 15}
            textColor={mutedText}
            axisStroke={axisStroke}
          />,
          'markers',
          'legends',
          'annotations',
        ]}
        tooltip={renderTooltip}
      />
    </div>
  )
}
